/**
 * Low-level Python source scanners (delimiters, strings, multiline calls, top-level splits).
 *
 * Layering in this repo:
 * - **This module** — lexical / structural scans with no product domain knowledge.
 * - **`hardwareParser.ts`** — orchestrates LSP + regex to build hardware/editor state from code.
 * - **`driveModeAutonomousActionUtils.ts`** — action labels, expressions, program-start calls.
 * - **`sensorCodeUtils.ts`** — sensor mapping regions and trigger chains.
 * - **`sensor-stream-tracker.ts`** — dashboard `set_request_list` sync.
 */

export interface ExtractedCallArgsText {
  argsText: string;
  endLineIndex: number;
}

export interface BalancedDelimiterSpan {
  inner: string;
  closeIndex: number;
}

export interface PythonCallLineRange {
  startLineIndex: number;
  endLineIndex: number;
}

export interface PythonEnclosingParenthesis extends PythonCallLineRange {
  calleePrefix: string | null;
  openParenIndex: number;
}

export interface PythonEnclosingCall extends PythonEnclosingParenthesis {
  calleePrefix: string;
}

export interface PythonDelimiterScanOptions {
  ignoreLineComments?: boolean;
}

export function splitCodeLines(code: string): string[] {
  return code.split(/\r?\n/);
}

export function skipWhitespaceInCode(code: string, index: number): number {
  while (index < code.length && /\s/.test(code[index] ?? '')) index++;
  return index;
}

const PYTHON_NUMERIC_LITERAL_PREFIX_RE = /^(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?/;
const MAX_NUMERIC_EXPRESSION_PAREN_DEPTH = 100;

/**
 * Evaluates a deliberately small, side-effect-free subset of Python numeric expressions.
 * Supported syntax is numeric literals, parentheses, unary +/- and binary +, -, *, and /.
 * Returns null unless the complete input is valid and produces a finite result.
 */
export function parsePythonConstantNumericExpression(rawValue: unknown): number | null {
  if (typeof rawValue === 'number') return Number.isFinite(rawValue) ? rawValue : null;

  const source = String(rawValue ?? '').trim();
  if (!source) return null;

  let index = 0;
  let parenthesisDepth = 0;

  const skipWhitespace = () => {
    while (index < source.length && /\s/.test(source[index] ?? '')) index++;
  };

  const parsePrimary = (): number | null => {
    skipWhitespace();
    if (source[index] === '(') {
      parenthesisDepth++;
      if (parenthesisDepth > MAX_NUMERIC_EXPRESSION_PAREN_DEPTH) return null;
      index++;
      const value = parseExpression();
      skipWhitespace();
      if (value === null || source[index] !== ')') return null;
      index++;
      parenthesisDepth--;
      return value;
    }

    const match = source.slice(index).match(PYTHON_NUMERIC_LITERAL_PREFIX_RE);
    if (!match) return null;
    index += match[0].length;
    const value = Number(match[0]);
    return Number.isFinite(value) ? value : null;
  };

  const parseUnary = (): number | null => {
    skipWhitespace();
    let sign = 1;
    while (source[index] === '+' || source[index] === '-') {
      if (source[index] === '-') sign *= -1;
      index++;
      skipWhitespace();
    }
    const value = parsePrimary();
    if (value === null) return null;
    const signedValue = sign * value;
    return Number.isFinite(signedValue) ? signedValue : null;
  };

  const parseProduct = (): number | null => {
    let value = parseUnary();
    if (value === null) return null;

    while (true) {
      skipWhitespace();
      const operator = source[index];
      if (operator !== '*' && operator !== '/') break;
      index++;
      const right = parseUnary();
      if (right === null || (operator === '/' && right === 0)) return null;
      value = operator === '*' ? value * right : value / right;
      if (!Number.isFinite(value)) return null;
    }

    return value;
  };

  function parseExpression(): number | null {
    let value = parseProduct();
    if (value === null) return null;

    while (true) {
      skipWhitespace();
      const operator = source[index];
      if (operator !== '+' && operator !== '-') break;
      index++;
      const right = parseProduct();
      if (right === null) return null;
      value = operator === '+' ? value + right : value - right;
      if (!Number.isFinite(value)) return null;
    }

    return value;
  }

  const value = parseExpression();
  skipWhitespace();
  return value !== null && index === source.length && parenthesisDepth === 0 ? value : null;
}

/** 0-based byte offset of `column` on `lineIndex` in a line array joined with `\n`. */
export function offsetAtLineColumn(lines: string[], lineIndex: number, column: number): number {
  let offset = 0;
  for (let i = 0; i < lineIndex; i++) {
    offset += (lines[i]?.length ?? 0) + 1;
  }
  return offset + column;
}

export function offsetAtCodeLineColumn(code: string, lineIndex: number, column: number): number {
  let offset = 0;
  for (let i = 0; i < lineIndex && offset < code.length; i++) {
    const nextLineIndex = code.indexOf('\n', offset);
    if (nextLineIndex < 0) return code.length;
    offset = nextLineIndex + 1;
  }
  return offset + column;
}

/** Leading whitespace on the line containing `index`. */
export function getIndentAtCodeIndex(code: string, index: number): string {
  const lineStart = code.lastIndexOf('\n', index - 1) + 1;
  const match = code.slice(lineStart, index).match(/^\s*/);
  return match?.[0] ?? '';
}

/** Extends `index` through trailing spaces/tabs and one line break. */
export function consumeThroughLineBreak(code: string, index: number): number {
  let end = index;
  while (end < code.length && (code[end] === ' ' || code[end] === '\t')) end++;
  if (code[end] === '\r') end++;
  if (code[end] === '\n') end++;
  return end;
}

type PythonQuote = '"' | "'";

interface PythonStringScanState {
  quote: PythonQuote | null;
  triple: boolean;
  raw: boolean;
  escaped: boolean;
}

function createPythonStringScanState(): PythonStringScanState {
  return { quote: null, triple: false, raw: false, escaped: false };
}

function resetPythonStringScanState(state: PythonStringScanState) {
  state.quote = null;
  state.triple = false;
  state.raw = false;
  state.escaped = false;
}

const PYTHON_STRING_PREFIXES = new Set(['r', 'u', 'b', 'f', 'br', 'rb', 'fr', 'rf']);

function isPythonIdentifierChar(ch: string): boolean {
  return /[A-Za-z0-9_]/.test(ch);
}

function findPythonStringLiteralStart(code: string, index: number): { quote: PythonQuote; triple: boolean; raw: boolean; consumed: number } | null {
  const ch = code[index] ?? '';
  if (ch === '"' || ch === "'") {
    const quote = ch as PythonQuote;
    const triple = code.startsWith(quote.repeat(3), index);
    return { quote, triple, raw: false, consumed: triple ? 3 : 1 };
  }

  if (!/[rRuUbBfF]/.test(ch)) return null;
  const prev = code[index - 1] ?? '';
  if (prev && isPythonIdentifierChar(prev)) return null;

  for (const prefixLength of [2, 1]) {
    const prefix = code.slice(index, index + prefixLength);
    if (!PYTHON_STRING_PREFIXES.has(prefix.toLowerCase())) continue;
    const quoteChar = code[index + prefixLength] ?? '';
    if (quoteChar !== '"' && quoteChar !== "'") continue;
    const quote = quoteChar as PythonQuote;
    const quoteIndex = index + prefixLength;
    const triple = code.startsWith(quote.repeat(3), quoteIndex);
    return {
      quote,
      triple,
      raw: prefix.toLowerCase().includes('r'),
      consumed: prefixLength + (triple ? 3 : 1),
    };
  }

  return null;
}

function hasOddBackslashRunBefore(code: string, index: number): boolean {
  let count = 0;
  for (let i = index - 1; i >= 0 && code[i] === '\\'; i--) {
    count++;
  }
  return count % 2 === 1;
}

function scanPythonStringLiteralAt(state: PythonStringScanState, code: string, index: number): number {
  const ch = code[index] ?? '';
  if (state.quote) {
    if (state.raw && !state.triple) {
      if (ch === state.quote && !hasOddBackslashRunBefore(code, index)) {
        resetPythonStringScanState(state);
      }
    } else if (state.escaped) {
      state.escaped = false;
    } else if ((!state.raw || code[index + 1] === state.quote) && ch === '\\') {
      state.escaped = true;
    } else if (state.triple) {
      if (code.startsWith(state.quote.repeat(3), index)) {
        resetPythonStringScanState(state);
        return 3;
      }
    } else if (ch === state.quote) {
      resetPythonStringScanState(state);
    }
    return 1;
  }

  const start = findPythonStringLiteralStart(code, index);
  if (start) {
    state.quote = start.quote;
    state.triple = start.triple;
    state.raw = start.raw;
    state.escaped = false;
    return start.consumed;
  }

  return 0;
}

export function isPlainPythonQuotedStringLiteral(value: string): boolean {
  return (
    ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'"))) &&
    !value.startsWith('"""') &&
    !value.startsWith("'''")
  );
}

/**
 * Returns the index of the closing delimiter matching `openChar` at `openIndex`, or -1.
 * Respects single- and double-quoted Python string literals. When `ignoreLineComments`
 * is true, delimiters after `#` outside a string are ignored through the line end.
 */
export function findMatchingDelimiterIndex(
  code: string,
  openIndex: number,
  openChar: string,
  closeChar: string,
  options: PythonDelimiterScanOptions = {}
): number {
  let depth = 0;
  const stringState = createPythonStringScanState();

  for (let i = openIndex; i < code.length; i++) {
    const ch = code[i] ?? '';
    const stringChars = scanPythonStringLiteralAt(stringState, code, i);
    if (stringChars > 0) {
      i += stringChars - 1;
      continue;
    }
    if (options.ignoreLineComments && ch === '#') {
      while (i < code.length && code[i] !== '\n' && code[i] !== '\r') i++;
      continue;
    }

    if (ch === openChar) depth++;
    else if (ch === closeChar) {
      depth--;
      if (depth === 0) return i;
    }
  }

  return -1;
}

/** Inner text and closing index for a balanced delimiter pair starting at `openIndex`. */
export function readBalancedDelimiterSpan(
  text: string,
  openIndex: number,
  openChar: string,
  closeChar: string,
  options: PythonDelimiterScanOptions = {}
): BalancedDelimiterSpan | null {
  if (text[openIndex] !== openChar) return null;
  const closeIndex = findMatchingDelimiterIndex(text, openIndex, openChar, closeChar, options);
  if (closeIndex === -1) return null;
  return { inner: text.slice(openIndex + 1, closeIndex), closeIndex };
}

export function findBalancedCallLineRanges(
  lines: string[],
  matchesCallLine: (line: string, lineIndex: number) => boolean,
  getOpenParenIndex: (line: string) => number | null
): PythonCallLineRange[] {
  const ranges: PythonCallLineRange[] = [];
  let lineIndex = 0;

  while (lineIndex < lines.length) {
    const line = lines[lineIndex] ?? '';
    if (!matchesCallLine(line, lineIndex)) {
      lineIndex++;
      continue;
    }

    const openParenIndex = getOpenParenIndex(line);
    if (openParenIndex === null || openParenIndex < 0) {
      lineIndex++;
      continue;
    }

    let depth = 1;
    const stringState = createPythonStringScanState();
    let currentLineIndex = lineIndex;
    let currentColumn = openParenIndex + 1;

    while (currentLineIndex < lines.length && depth > 0) {
      const content = lines[currentLineIndex] ?? '';
      for (let i = currentColumn; i < content.length; i++) {
        const ch = content[i];
        const stringChars = scanPythonStringLiteralAt(stringState, content, i);
        if (stringChars > 0) {
          i += stringChars - 1;
          continue;
        }

        if (ch === '#') break;

        if (ch === '(') depth++;
        else if (ch === ')') {
          depth--;
          if (depth === 0) {
            ranges.push({ startLineIndex: lineIndex, endLineIndex: currentLineIndex });
            lineIndex = currentLineIndex;
            break;
          }
        }
      }
      currentLineIndex++;
      currentColumn = 0;
    }

    lineIndex++;
  }

  return ranges;
}

export function findUnmatchedOpenParenIndices(line: string): number[] {
  const stack: number[] = [];
  const stringState = createPythonStringScanState();

  for (let i = 0; i < line.length; i++) {
    const ch = line[i] ?? '';
    const stringChars = scanPythonStringLiteralAt(stringState, line, i);
    if (stringChars > 0) {
      i += stringChars - 1;
      continue;
    }

    if (ch === '#') break;
    if (ch === '(') stack.push(i);
    else if (ch === ')' && stack.length > 0) stack.pop();
  }

  return stack;
}

export function findFirstUnmatchedOpenParenIndex(line: string): number | null {
  return findUnmatchedOpenParenIndices(line)[0] ?? null;
}

/**
 * Matches a `def`, `async def`, or `class` header up to its name. Both keywords
 * are reserved, so a prefix starting the line this way is always a declaration
 * and never a call expression.
 */
const PYTHON_DECLARATION_HEADER_RE = /^\s*(?:async\s+)?(?:def|class)\s+[A-Za-z_]\w*$/;

/** True when text preceding an open parenthesis is a call target rather than a declaration header. */
function isCallLikeCalleePrefix(prefix: string): boolean {
  return /(?:[A-Za-z_]\w*|\)|\])$/.test(prefix) && !PYTHON_DECLARATION_HEADER_RE.test(prefix);
}

/**
 * Returns balanced multiline parentheses that enclose a 0-based insertion line.
 *
 * Results are ordered outermost to innermost. `calleePrefix` is populated only
 * when the opening parenthesis follows a call-like expression; declaration
 * headers delimit parameter and base-class lists, so they stay `null`. One
 * lexical state is carried across the complete source so multiline strings
 * cannot masquerade as program structure.
 */
export function findEnclosingPythonParenthesesAtLine(lines: string[], targetLineIndex: number): PythonEnclosingParenthesis[] {
  const enclosing: PythonEnclosingParenthesis[] = [];
  const stack: Array<{
    startLineIndex: number;
    openParenIndex: number;
    calleePrefix: string | null;
  }> = [];
  const stringState = createPythonStringScanState();

  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    const line = lines[lineIndex] ?? '';
    for (let column = 0; column < line.length; column++) {
      const ch = line[column] ?? '';
      const stringChars = scanPythonStringLiteralAt(stringState, line, column);
      if (stringChars > 0) {
        column += stringChars - 1;
        continue;
      }

      if (ch === '#') break;
      if (ch === '(') {
        const prefix = line.slice(0, column).trimEnd();
        stack.push({
          startLineIndex: lineIndex,
          openParenIndex: column,
          calleePrefix: isCallLikeCalleePrefix(prefix) ? prefix : null,
        });
        continue;
      }
      if (ch !== ')') continue;

      const opening = stack.pop();
      if (!opening || targetLineIndex <= opening.startLineIndex || targetLineIndex > lineIndex) continue;
      enclosing.push({ ...opening, endLineIndex: lineIndex });
    }
  }

  return enclosing.sort(
    (left, right) =>
      left.startLineIndex - right.startLineIndex || left.openParenIndex - right.openParenIndex || right.endLineIndex - left.endLineIndex
  );
}

/** Returns only call-like entries from `findEnclosingPythonParenthesesAtLine`. */
export function findEnclosingPythonCallsAtLine(lines: string[], targetLineIndex: number): PythonEnclosingCall[] {
  return findEnclosingPythonParenthesesAtLine(lines, targetLineIndex).filter(
    (parenthesis): parenthesis is PythonEnclosingCall => parenthesis.calleePrefix !== null
  );
}

type SplitTopLevelByCommaOptions = {
  ignoreLineComments?: boolean;
};

/**
 * Splits on commas at top-level delimiter depth, respecting quoted strings and backslash escapes.
 * When configured, Python `#` comments outside strings are skipped through the line end.
 */
export function splitTopLevelByComma(value: string, options: SplitTopLevelByCommaOptions = {}): string[] {
  const items: string[] = [];
  let current = '';
  let parenDepth = 0;
  let bracketDepth = 0;
  let braceDepth = 0;
  const stringState = createPythonStringScanState();

  for (let i = 0; i < value.length; i++) {
    const ch = value[i] ?? '';
    const stringChars = scanPythonStringLiteralAt(stringState, value, i);
    if (stringChars > 0) {
      current += value.slice(i, i + stringChars);
      i += stringChars - 1;
      continue;
    }

    if (options.ignoreLineComments && ch === '#') {
      while (i + 1 < value.length && value[i + 1] !== '\n') i++;
      continue;
    }

    // Track all bracket kinds so commas inside tuples, lists, or dicts
    // (e.g. `lines=[(1, "Ready"), (2, "Go")]`) are not treated as top-level separators.
    if (ch === '(') parenDepth++;
    else if (ch === ')' && parenDepth > 0) parenDepth--;
    else if (ch === '[') bracketDepth++;
    else if (ch === ']' && bracketDepth > 0) bracketDepth--;
    else if (ch === '{') braceDepth++;
    else if (ch === '}' && braceDepth > 0) braceDepth--;
    else if (ch === ',' && parenDepth === 0 && bracketDepth === 0 && braceDepth === 0) {
      items.push(current.trim());
      current = '';
      continue;
    }

    current += ch;
  }
  if (current.trim()) items.push(current.trim());
  return items;
}

/** Returns whether a complete call expression has an empty top-level keyword argument. */
export function hasUnresolvedKeywordArgument(code: string): boolean {
  const callMatch = code.match(/^[A-Za-z_][\w]*(?:\.[A-Za-z_][\w]*)*\s*\(/);
  if (!callMatch) return false;

  const openParenIndex = code.indexOf('(', callMatch.index ?? 0);
  const closeParenIndex = findMatchingDelimiterIndex(code, openParenIndex, '(', ')', { ignoreLineComments: true });
  if (closeParenIndex < 0) return false;

  return splitTopLevelByComma(code.slice(openParenIndex + 1, closeParenIndex), { ignoreLineComments: true }).some((argument) =>
    /^[A-Za-z_]\w*\s*=\s*$/.test(argument.trim())
  );
}

export interface PythonSourceTextSegment {
  text: string;
  /** Inclusive index of the trimmed segment in the source text. */
  startIndex: number;
  /** Exclusive index of the trimmed segment in the source text. */
  endIndex: number;
}

/**
 * Splits a Python simple-statement list on top-level semicolons while preserving
 * source offsets. Semicolons inside strings or nested delimiters are ignored.
 */
export function splitTopLevelBySemicolon(value: string): PythonSourceTextSegment[] {
  const segments: PythonSourceTextSegment[] = [];
  let segmentStart = 0;
  let parenDepth = 0;
  let bracketDepth = 0;
  let braceDepth = 0;
  const stringState = createPythonStringScanState();

  const pushSegment = (segmentEnd: number) => {
    let startIndex = segmentStart;
    let endIndex = segmentEnd;
    while (startIndex < endIndex && /\s/.test(value[startIndex] ?? '')) startIndex++;
    while (endIndex > startIndex && /\s/.test(value[endIndex - 1] ?? '')) endIndex--;
    if (startIndex < endIndex) {
      segments.push({ text: value.slice(startIndex, endIndex), startIndex, endIndex });
    }
  };

  for (let i = 0; i < value.length; i++) {
    const ch = value[i] ?? '';
    const stringChars = scanPythonStringLiteralAt(stringState, value, i);
    if (stringChars > 0) {
      i += stringChars - 1;
      continue;
    }

    if (ch === '(') parenDepth++;
    else if (ch === ')' && parenDepth > 0) parenDepth--;
    else if (ch === '[') bracketDepth++;
    else if (ch === ']' && bracketDepth > 0) bracketDepth--;
    else if (ch === '{') braceDepth++;
    else if (ch === '}' && braceDepth > 0) braceDepth--;
    else if (ch === ';' && parenDepth === 0 && bracketDepth === 0 && braceDepth === 0) {
      pushSegment(i);
      segmentStart = i + 1;
    }
  }

  pushSegment(value.length);
  return segments;
}

/**
 * Returns the index of a top-level Python keyword-assignment `=` operator, or -1.
 * Ignores string literals, nested delimiters, and comparison/walrus operators.
 */
export function findTopLevelKeywordAssignmentIndex(value: string): number {
  let parenDepth = 0;
  let bracketDepth = 0;
  let braceDepth = 0;
  const stringState = createPythonStringScanState();

  for (let i = 0; i < value.length; i++) {
    const ch = value[i] ?? '';
    const stringChars = scanPythonStringLiteralAt(stringState, value, i);
    if (stringChars > 0) {
      i += stringChars - 1;
      continue;
    }

    if (ch === '(') parenDepth++;
    else if (ch === ')' && parenDepth > 0) parenDepth--;
    else if (ch === '[') bracketDepth++;
    else if (ch === ']' && bracketDepth > 0) bracketDepth--;
    else if (ch === '{') braceDepth++;
    else if (ch === '}' && braceDepth > 0) braceDepth--;
    else if (ch === '=' && parenDepth === 0 && bracketDepth === 0 && braceDepth === 0) {
      const prev = value[i - 1] ?? '';
      const next = value[i + 1] ?? '';
      if (prev !== '=' && prev !== '!' && prev !== '<' && prev !== '>' && prev !== ':' && next !== '=') return i;
    }
  }

  return -1;
}

/**
 * Strip formatting whitespace and trailing commas before `)` / `]` outside string literals
 * so `(0, 0, 0,)` matches `(0,0,0)` while `'a b'` stays distinct from `'ab'`.
 */
export function normalizePythonCallCompact(text: string): string {
  let result = '';
  const stringState = createPythonStringScanState();

  for (let i = 0; i < text.length; i++) {
    const stringChars = scanPythonStringLiteralAt(stringState, text, i);
    if (stringChars > 0) {
      result += text.slice(i, i + stringChars);
      i += stringChars - 1;
      continue;
    }

    const ch = text[i] ?? '';
    if (ch === '#') {
      while (i + 1 < text.length && text[i + 1] !== '\n' && text[i + 1] !== '\r') i += 1;
      continue;
    }
    if (/\s/.test(ch)) continue;

    if (ch === ',') {
      let lookAhead = i + 1;
      while (lookAhead < text.length) {
        const lookCh = text[lookAhead] ?? '';
        if (/\s/.test(lookCh)) {
          lookAhead += 1;
          continue;
        }
        if (lookCh === '#') {
          while (lookAhead < text.length && text[lookAhead] !== '\n' && text[lookAhead] !== '\r') lookAhead += 1;
          continue;
        }
        break;
      }
      const next = text[lookAhead] ?? '';
      if (next === ')' || next === ']') continue;
    }

    result += ch;
  }

  return result;
}

/**
 * True when a compact arg token appears as a whole argument in a compact call span.
 * Avoids `velocity=1` matching inside `velocity=10` by requiring arg-token boundaries.
 */
export function compactSpanContainsArgToken(compactSpan: string, compactToken: string): boolean {
  if (!compactToken) return false;
  const escaped = compactToken.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(String.raw`(?:^|[(,])${escaped}(?=[),]|$)`).test(compactSpan);
}

type PythonOutsideStringCharAction = 'continue' | 'stop' | 'skip-rest-of-line';

function scanPythonCodeRespectingStrings(
  code: string,
  handlers: {
    onString?: (slice: string) => void;
    onChar?: (ch: string) => PythonOutsideStringCharAction;
  }
): void {
  const stringState = createPythonStringScanState();

  for (let i = 0; i < code.length; i++) {
    const stringChars = scanPythonStringLiteralAt(stringState, code, i);
    if (stringChars > 0) {
      handlers.onString?.(code.slice(i, i + stringChars));
      i += stringChars - 1;
      continue;
    }

    const ch = code[i] ?? '';
    const action = handlers.onChar?.(ch) ?? 'continue';
    if (action === 'stop') return;
    if (action === 'skip-rest-of-line') {
      while (i < code.length && code[i] !== '\n' && code[i] !== '\r') i++;
    }
  }
}

/** Returns `value` trimmed with any trailing `#` comment removed (string-literal safe). */
export function stripTrailingPythonLineComment(value: string): string {
  return stripPythonLineCommentsOutsideStrings(value).trim();
}

/**
 * Returns the trailing `#` comment on a single physical line (from `#` through end), or `''`.
 * String-literal safe. Does not include whitespace before `#`.
 */
export function extractTrailingPythonLineComment(line: string): string {
  const stringState = createPythonStringScanState();
  for (let i = 0; i < line.length; i++) {
    const stringChars = scanPythonStringLiteralAt(stringState, line, i);
    if (stringChars > 0) {
      i += stringChars - 1;
      continue;
    }
    if (line[i] === '#') return line.slice(i).trimEnd();
  }
  return '';
}

/**
 * Advance `()` / `[]` / `{}` nesting depth across `code` (string-aware).
 * Used when classifying trailing comments as outer call-arg vs nested tuple/list.
 */
export function advancePythonDelimiterNestingDepth(startDepth: number, code: string): number {
  let depth = startDepth;
  const stringState = createPythonStringScanState();
  for (let i = 0; i < code.length; i++) {
    const stringChars = scanPythonStringLiteralAt(stringState, code, i);
    if (stringChars > 0) {
      i += stringChars - 1;
      continue;
    }
    const ch = code[i] ?? '';
    if (ch === '(' || ch === '[' || ch === '{') depth++;
    else if ((ch === ')' || ch === ']' || ch === '}') && depth > 0) depth--;
  }
  return depth;
}

function pythonLineIndentColumn(line: string): number {
  return (line.match(/^\s*/)?.[0] ?? '').replace(/\t/g, '    ').length;
}

export function isOffsetInPythonStringOrLineComment(code: string, offset: number): boolean {
  const stringState = createPythonStringScanState();
  for (let i = 0; i < Math.min(offset, code.length); i++) {
    const ch = code[i] ?? '';
    const stringChars = scanPythonStringLiteralAt(stringState, code, i);
    if (stringChars > 0) {
      if (i + stringChars > offset) return true;
      i += stringChars - 1;
      continue;
    }
    if (ch === '#') {
      while (i < code.length && i < offset && code[i] !== '\n' && code[i] !== '\r') i++;
      if (i >= offset) return true;
    }
  }
  return Boolean(stringState.quote);
}

/** Parses double-quoted Python string literals from a list/tuple inner fragment. */
export function parseDoubleQuotedStringLiterals(inner: string): string[] {
  const keys: string[] = [];
  const re = /"((?:[^"\\]|\\.)*)"/g;
  let match: RegExpExecArray | null = null;
  while ((match = re.exec(inner))) {
    keys.push(unescapePythonDoubleQuotedLiteral(match[1] ?? ''));
  }
  return keys;
}

/** True when `code` contains a Python `#` line comment outside quoted string literals. */
export function containsPythonLineCommentOutsideStrings(code: string): boolean {
  let found = false;

  scanPythonCodeRespectingStrings(code, {
    onChar: (ch) => {
      if (ch === '#') {
        found = true;
        return 'stop';
      }
      return 'continue';
    },
  });

  return found;
}

/** Removes Python `#` line comments outside quoted string literals. Newlines are preserved. */
export function stripPythonLineCommentsOutsideStrings(code: string): string {
  let out = '';

  scanPythonCodeRespectingStrings(code, {
    onString: (slice) => {
      out += slice;
    },
    onChar: (ch) => {
      if (ch === '#') return 'skip-rest-of-line';
      out += ch;
      return 'continue';
    },
  });

  return out;
}

function unescapePythonDoubleQuotedLiteral(value: string): string {
  return value.replace(/\\"/g, '"').replace(/\\\\/g, '\\');
}

/**
 * Returns the unescaped string value of a plain (non-triple-quoted) Python string literal.
 * Strips the outer quote characters and unescapes `\\` and the relevant quote escape sequence.
 * Accepts values already validated by `isPlainPythonQuotedStringLiteral`.
 */
export function parsePythonPlainStringLiteralValue(literal: string): string {
  const quote = literal[0] as '"' | "'";
  const inner = literal.slice(1, -1);
  if (quote === '"') {
    return inner.replace(/\\"/g, '"').replace(/\\\\/g, '\\');
  }
  return inner.replace(/\\'/g, "'").replace(/\\\\/g, '\\');
}

/**
 * Extracts the inner text of a balanced `(...)` call that may span multiple lines.
 * Whitespace between lines is normalized to a single space (matches editor parser behavior).
 * When `ignoreLineComments` is true, `#` comments are skipped through the line end.
 */
export function extractCallArgsText(
  lines: string[],
  startLineIndex: number,
  openParenIndex: number,
  options: PythonDelimiterScanOptions = {}
): ExtractedCallArgsText | null {
  let depth = 1;
  const stringState = createPythonStringScanState();
  let curLineIndex = startLineIndex;
  let curCharIndex = openParenIndex + 1;
  const chunks: string[] = [];

  while (curLineIndex < lines.length && depth > 0) {
    const content = lines[curLineIndex] ?? '';
    let hitLineComment = false;
    for (let i = curCharIndex; i < content.length; i++) {
      const ch = content[i];
      const stringChars = scanPythonStringLiteralAt(stringState, content, i);
      if (stringChars > 0) {
        i += stringChars - 1;
        continue;
      }

      if (options.ignoreLineComments && ch === '#') {
        hitLineComment = true;
        break;
      }

      if (ch === '(') depth++;
      else if (ch === ')') {
        depth--;
        if (depth === 0) {
          chunks.push(content.slice(curCharIndex, i));
          const argsText = chunks
            .map((s) => s.trim())
            .filter((s) => s.length > 0)
            .join(' ')
            .replace(/\s+/g, ' ')
            .trim();
          return { argsText, endLineIndex: curLineIndex };
        }
      }
    }
    if (depth > 0 && !hitLineComment) {
      const segment = curLineIndex === startLineIndex ? content.slice(openParenIndex + 1) : content;
      if (segment.trim().length) chunks.push(segment.trim());
    }
    curLineIndex++;
    curCharIndex = 0;
  }

  return null;
}

/** Matches constructor assignments like `varName = ClassName(`, including optional type annotations and qualified class names. */
export const VAR_ASSIGN_RE = /^\s*([a-zA-Z_][\w]*)\s*(?::\s*[^=]+?)?=\s*(?:[A-Za-z_][\w]*\.)*([A-Za-z_][\w]*)\s*\(/;

export type FindVarAssignmentLineIndexOpts = {
  /** When set, only matches declarations whose constructor class is in this set. */
  allowedClassNames?: ReadonlySet<string>;
  /** When set, ignores declarations and rebinds at or after this 0-based line index. */
  beforeLineIndex?: number;
  /** `last` (default) keeps the latest allowed constructor; `first` keeps the earliest unless later rebound. */
  selection?: 'first' | 'last';
  /** `clear` (default) drops the match on a later disallowed constructor; `skip` keeps the prior allowed match. */
  disallowedConstructor?: 'clear' | 'skip';
};

export type TopLevelVarAssignment = {
  lineIndex: number;
  variableName: string;
  className: string;
};

export function leadingWhitespaceLength(line: string): number {
  const match = /^(\s*)/.exec(line);
  return match ? match[1].length : 0;
}

/** True when `line` is executable top-level program code (not blank or a comment). */
export function isTopLevelCodeLine(line: string): boolean {
  const trimmed = line.trim();
  if (!trimmed || trimmed.startsWith('#')) return false;
  return leadingWhitespaceLength(line) === 0;
}

/** Matches top-level assignments like `varName = ...`, including non-constructor right-hand sides. */
const TOP_LEVEL_VAR_ASSIGN_RE = /^\s*([a-zA-Z_][\w]*)\s*(?::\s*[^=]+?)?=(?!=)/;

export interface PythonSimpleAssignment {
  variableName: string;
  constructorClassName: string | null;
}

/** Parses a direct simple-name assignment, preserving a direct constructor class when present. */
export function parseSimplePythonAssignment(statementText: string): PythonSimpleAssignment | null {
  const constructorMatch = statementText.match(VAR_ASSIGN_RE);
  if (constructorMatch?.[1] && constructorMatch?.[2]) {
    return { variableName: constructorMatch[1], constructorClassName: constructorMatch[2] };
  }
  const assignmentMatch = statementText.match(TOP_LEVEL_VAR_ASSIGN_RE);
  if (!assignmentMatch?.[1]) return null;
  return { variableName: assignmentMatch[1], constructorClassName: null };
}

/** Simple names bound by a `for` target, including tuple/list destructuring targets. */
export function collectSimplePythonForTargetNames(statementText: string): Set<string> {
  const targetMatch = statementText.match(/^(?:async\s+)?for\s+(.+?)\s+in\b/);
  if (!targetMatch?.[1]) return new Set();
  const names = new Set<string>();
  const simpleNameRe = /(?:^|[^.\w])([A-Za-z_][\w]*)(?![.\w])/g;
  let match: RegExpExecArray | null = null;
  while ((match = simpleNameRe.exec(targetMatch[1]))) {
    if (match[1]) names.add(match[1]);
  }
  return names;
}

type TopLevelVarAssignmentEvent =
  | { kind: 'constructor'; lineIndex: number; variableName: string; className: string }
  | { kind: 'rebind'; lineIndex: number; variableName: string };

function collectLineStartInsidePythonStringFlags(lines: string[]): boolean[] {
  const code = stripPythonLineCommentsOutsideStrings(lines.join('\n'));
  const flags = new Array(lines.length).fill(false);
  const stringState = createPythonStringScanState();

  let lineIndex = 0;
  let lineStartedInsideString = false;

  for (let i = 0; i < code.length; ) {
    if (i === 0 || code[i - 1] === '\n') {
      lineStartedInsideString = !!stringState.quote;
      if (lineIndex < flags.length) flags[lineIndex] = lineStartedInsideString;
    }

    const consumed = scanPythonStringLiteralAt(stringState, code, i);
    if (consumed > 0) {
      for (let j = i; j < i + consumed; j++) {
        if (code[j] === '\n') lineIndex++;
      }
      i += consumed;
      continue;
    }

    if (code[i] === '\n') {
      lineIndex++;
    }
    i++;
  }

  return flags;
}

function scanTopLevelVarAssignmentEvents(lines: string[]): TopLevelVarAssignmentEvent[] {
  const events: TopLevelVarAssignmentEvent[] = [];
  const lineStartsInsideString = collectLineStartInsidePythonStringFlags(lines);
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i] ?? '';
    if (lineStartsInsideString[i]) continue;
    if (!isTopLevelCodeLine(line)) continue;
    const assignment = parseSimplePythonAssignment(line);
    if (assignment?.constructorClassName) {
      events.push({
        kind: 'constructor',
        lineIndex: i,
        variableName: assignment.variableName,
        className: assignment.constructorClassName,
      });
      continue;
    }
    if (assignment) {
      events.push({ kind: 'rebind', lineIndex: i, variableName: assignment.variableName });
    }
  }
  return events;
}

function readTopLevelConstructorAssignments(lines: string[]): TopLevelVarAssignment[] {
  return scanTopLevelVarAssignmentEvents(lines)
    .filter((event): event is Extract<TopLevelVarAssignmentEvent, { kind: 'constructor' }> => event.kind === 'constructor')
    .map(({ lineIndex, variableName, className }) => ({ lineIndex, variableName, className }));
}

/** Top-level `variableName = ClassName(...)` declarations in source order. */
export function collectTopLevelVarAssignments(lines: string[], opts?: FindVarAssignmentLineIndexOpts): TopLevelVarAssignment[] {
  return readTopLevelConstructorAssignments(lines).filter(
    (assignment) => !opts?.allowedClassNames || opts.allowedClassNames.has(assignment.className)
  );
}

/** Latest top-level declaration per variable name (last occurrence wins). */
export function collectLastTopLevelVarAssignmentsByName(lines: string[], opts?: FindVarAssignmentLineIndexOpts): TopLevelVarAssignment[] {
  const effective = new Map<string, TopLevelVarAssignment>();
  for (const event of scanTopLevelVarAssignmentEvents(lines)) {
    if (typeof opts?.beforeLineIndex === 'number' && event.lineIndex >= opts.beforeLineIndex) break;
    if (event.kind === 'rebind') {
      effective.delete(event.variableName);
      continue;
    }
    if (opts?.allowedClassNames && !opts.allowedClassNames.has(event.className)) {
      effective.delete(event.variableName);
      continue;
    }
    effective.set(event.variableName, {
      lineIndex: event.lineIndex,
      variableName: event.variableName,
      className: event.className,
    });
  }
  return Array.from(effective.values());
}

/** 0-based line index of a top-level `variableName = ClassName(...)`, or null. */
export function findVarAssignmentLineIndex(lines: string[], variableName: string, opts?: FindVarAssignmentLineIndexOpts): number | null {
  const selection = opts?.selection ?? 'last';
  const disallowedConstructor = opts?.disallowedConstructor ?? 'clear';
  let firstIndex: number | null = null;
  let lastIndex: number | null = null;

  for (const event of scanTopLevelVarAssignmentEvents(lines)) {
    if (typeof opts?.beforeLineIndex === 'number' && event.lineIndex >= opts.beforeLineIndex) break;
    if (event.variableName !== variableName) continue;
    if (event.kind === 'rebind') {
      firstIndex = null;
      lastIndex = null;
      continue;
    }
    const classAllowed = !opts?.allowedClassNames || opts.allowedClassNames.has(event.className);
    if (!classAllowed) {
      if (disallowedConstructor === 'clear') {
        firstIndex = null;
        lastIndex = null;
      }
      continue;
    }
    if (firstIndex === null) firstIndex = event.lineIndex;
    lastIndex = event.lineIndex;
  }

  return selection === 'first' ? firstIndex : lastIndex;
}

/** True when `variableName` is reassigned by any top-level assignment after the given 0-based line index. */
export function hasTopLevelVarAssignmentAfterLine(lines: string[], variableName: string, afterLineIndex: number): boolean {
  for (const event of scanTopLevelVarAssignmentEvents(lines)) {
    if (event.lineIndex <= afterLineIndex) continue;
    if (event.variableName === variableName) return true;
  }
  return false;
}

/** 0-based line index of the next top-level assignment to `variableName` after `afterLineIndex`, or null. */
export function findNextTopLevelVarAssignmentLineIndex(lines: string[], variableName: string, afterLineIndex: number): number | null {
  for (const event of scanTopLevelVarAssignmentEvents(lines)) {
    if (event.lineIndex <= afterLineIndex) continue;
    if (event.variableName === variableName) return event.lineIndex;
  }
  return null;
}

export type ResolveConstructorDeclarationLineOpts = {
  constructorClassName?: string;
  fallbackLineIndex0Based?: number;
};

function matchesConstructorDeclarationLine(lines: string[], lineIndex: number, variableName: string, constructorClassName?: string): boolean {
  const match = (lines[lineIndex] ?? '').match(VAR_ASSIGN_RE);
  if (match?.[1] !== variableName) return false;
  if (constructorClassName && match?.[2] !== constructorClassName) return false;
  return true;
}

/** Resolves the constructor declaration line for device edits, preferring the card's class when provided. */
export function resolveConstructorDeclarationLineIndex(
  lines: string[],
  variableName: string,
  opts?: ResolveConstructorDeclarationLineOpts
): number | null {
  const { constructorClassName, fallbackLineIndex0Based } = opts ?? {};
  const lookupOpts = constructorClassName
    ? { allowedClassNames: new Set([constructorClassName]), disallowedConstructor: 'skip' as const }
    : undefined;

  const declLine = findVarAssignmentLineIndex(lines, variableName, lookupOpts);

  if (declLine === null && fallbackLineIndex0Based !== undefined && Number.isFinite(fallbackLineIndex0Based)) {
    if (matchesConstructorDeclarationLine(lines, fallbackLineIndex0Based, variableName, constructorClassName)) {
      return fallbackLineIndex0Based;
    }
  }

  return declLine;
}

type TextModelLike = {
  getValue?: () => string;
  getLineCount: () => number;
  getLineContent: (lineNumber: number) => string;
};

export function getTextModelLines(model: TextModelLike): string[] {
  if (typeof model.getValue === 'function') return splitCodeLines(model.getValue());
  const lines: string[] = [];
  for (let i = 1; i <= model.getLineCount(); i++) {
    lines.push(model.getLineContent(i));
  }
  return lines;
}

/** 1-based line after the last matching top-level constructor declaration, or null. */
export function findTopLevelVarDeclarationEndLine(model: TextModelLike, variableName: string, opts?: FindVarAssignmentLineIndexOpts): number | null {
  const lines = getTextModelLines(model);
  const declLine = findVarAssignmentLineIndex(lines, variableName, opts);
  if (declLine === null) return null;
  return findConstructorCallEndLineIndex(lines, declLine) + 1;
}

export type TopLevelVarDeclarationAnchor = {
  variableName: string;
  allowedClassNames?: ReadonlySet<string>;
  preferLastEffectiveDeclaration?: boolean;
};

export function findTopLevelVarDeclarationEndLineForAnchor(model: TextModelLike, anchor: TopLevelVarDeclarationAnchor): number | null {
  return findTopLevelVarDeclarationEndLine(model, anchor.variableName, {
    allowedClassNames: anchor.allowedClassNames,
    selection: anchor.preferLastEffectiveDeclaration ? 'last' : 'first',
    disallowedConstructor: anchor.preferLastEffectiveDeclaration ? 'clear' : 'skip',
  });
}

/** Bump `insertLine` past declared action-target constructors referenced by `anchors`. */
export function bumpInsertLineForTopLevelVarDeclarationAnchors(
  model: TextModelLike,
  insertLine: number,
  anchors: ReadonlyArray<TopLevelVarDeclarationAnchor>
): number {
  let line = insertLine;
  for (const anchor of anchors) {
    const targetEndLine = findTopLevelVarDeclarationEndLineForAnchor(model, anchor);
    if (targetEndLine === null) continue;
    line = Math.max(line, targetEndLine + 1);
  }
  return line;
}

/** 0-based end line of a constructor call starting at `startLineIndex` (inclusive). */
export function findConstructorCallEndLineIndex(lines: readonly string[], startLineIndex: number): number {
  let depth = 0;
  const stringState = createPythonStringScanState();
  for (let lineNum = startLineIndex; lineNum < lines.length; lineNum++) {
    const line = lines[lineNum] ?? '';
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      const stringChars = scanPythonStringLiteralAt(stringState, line, i);
      if (stringChars > 0) {
        i += stringChars - 1;
        continue;
      }

      if (ch === '#') break;

      if (ch === '(') depth++;
      else if (ch === ')') {
        depth--;
        if (depth === 0) return lineNum;
      }
    }
  }
  return startLineIndex;
}

/** 1-based end line of a constructor call starting at `startLineOneBased` (inclusive). */
export function findConstructorCallEndLine(model: TextModelLike, startLineOneBased: number): number {
  const lineCount = model.getLineCount();
  if (!Number.isFinite(startLineOneBased) || startLineOneBased < 1 || startLineOneBased > lineCount) {
    return startLineOneBased;
  }
  const lines = getTextModelLines(model);
  return findConstructorCallEndLineIndex(lines, startLineOneBased - 1) + 1;
}

function findConstructorKwargValueEnd(
  lines: string[],
  startLineIndex: number,
  valueStartCol: number,
  endLineIndex: number
): { lineIndex: number; col: number } {
  let depth = 0;
  const stringState = createPythonStringScanState();

  for (let lineNum = startLineIndex; lineNum <= endLineIndex; lineNum++) {
    const line = lines[lineNum] ?? '';
    const startCol = lineNum === startLineIndex ? Math.max(valueStartCol, 0) : 0;
    for (let i = startCol; i < line.length; i++) {
      const ch = line[i];
      const stringChars = scanPythonStringLiteralAt(stringState, line, i);
      if (stringChars > 0) {
        i += stringChars - 1;
        continue;
      }

      if (ch === '(' || ch === '[' || ch === '{') {
        depth++;
        continue;
      }
      if (ch === ')' || ch === ']' || ch === '}') {
        if (depth === 0) return { lineIndex: lineNum, col: i };
        depth--;
        continue;
      }
      if (ch === ',' && depth === 0) return { lineIndex: lineNum, col: i };
    }
  }

  const finalLine = Math.min(endLineIndex, lines.length - 1);
  const finalLineContent = lines[finalLine] ?? '';
  return { lineIndex: finalLine, col: finalLineContent.length };
}

export interface ConstructorKwargSpan {
  startOffset: number;
  endOffset: number;
}

/** Byte span covering `kwargName=…` inside the constructor at `declarationLineIndex`. */
export function findConstructorKwargSpan(code: string, declarationLineIndex: number, kwargName: string): ConstructorKwargSpan | null {
  const lines = splitCodeLines(code);
  const endLineIndex = findConstructorCallEndLineIndex(lines, declarationLineIndex);

  for (let lineNum = declarationLineIndex; lineNum <= endLineIndex; lineNum++) {
    const line = lines[lineNum] ?? '';
    let searchIndex = 0;
    while (searchIndex < line.length) {
      const parameterArg = line.indexOf(kwargName, searchIndex);
      if (parameterArg < 0) break;

      const before = parameterArg > 0 ? line[parameterArg - 1] : '';
      const after = parameterArg + kwargName.length < line.length ? line[parameterArg + kwargName.length] : '';
      if (/[A-Za-z0-9_]/.test(before) || /[A-Za-z0-9_]/.test(after)) {
        searchIndex = parameterArg + kwargName.length;
        continue;
      }

      const equals = line.indexOf('=', parameterArg + kwargName.length);
      if (equals < 0) {
        searchIndex = parameterArg + kwargName.length;
        continue;
      }

      const endPos = findConstructorKwargValueEnd(lines, lineNum, equals + 1, endLineIndex);
      return {
        startOffset: offsetAtLineColumn(lines, lineNum, parameterArg),
        endOffset: offsetAtLineColumn(lines, endPos.lineIndex, endPos.col),
      };
    }
  }

  return null;
}

export function readConstructorArgumentValue(
  code: string | undefined,
  declarationLineIndex: number,
  keywordName: string,
  positionalIndex?: number
): string | null | undefined {
  if (!code) return undefined;
  const lines = splitCodeLines(code);
  const line = lines[declarationLineIndex] ?? '';
  const openParenIndex = line.indexOf('(');
  if (openParenIndex < 0) return null;

  const openOffset = offsetAtCodeLineColumn(code, declarationLineIndex, openParenIndex);
  const span = readBalancedDelimiterSpan(code, openOffset, '(', ')', { ignoreLineComments: true });
  if (!span) return null;

  let positionalValue: string | undefined;
  let currentPositionalIndex = 0;
  for (const token of splitTopLevelByComma(span.inner, { ignoreLineComments: true })) {
    const assignmentIndex = findTopLevelKeywordAssignmentIndex(token);
    if (assignmentIndex > 0) {
      const key = token.slice(0, assignmentIndex).trim();
      if (key === keywordName) return token.slice(assignmentIndex + 1).trim();
      continue;
    }

    const value = token.trim();
    if (!value) continue;
    if (currentPositionalIndex === positionalIndex) positionalValue = value;
    currentPositionalIndex++;
  }

  return positionalValue;
}

export function readConstructorKeywordValue(code: string | undefined, declarationLineIndex: number, keywordName: string): string | null | undefined {
  return readConstructorArgumentValue(code, declarationLineIndex, keywordName);
}

function escapePythonScanRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function readPropertyAssignmentValue(
  code: string | undefined,
  targetName: string,
  propertyName: string,
  startLineIndex: number,
  endLineIndex = Number.POSITIVE_INFINITY
): string | undefined {
  if (!code || !targetName || !propertyName) return undefined;
  const lines = splitCodeLines(code);
  const assignmentRe = new RegExp(`^\\s*${escapePythonScanRegExp(targetName)}\\.${escapePythonScanRegExp(propertyName)}\\s*=`);
  const declarationIndent = pythonLineIndentColumn(lines[startLineIndex] ?? '');
  let value: string | undefined;

  for (let lineIndex = Math.max(0, startLineIndex + 1); lineIndex < Math.min(lines.length, endLineIndex); lineIndex++) {
    const line = lines[lineIndex] ?? '';
    if (pythonLineIndentColumn(line) > declarationIndent) continue;
    if (!assignmentRe.test(line)) continue;
    const targetOffset = offsetAtCodeLineColumn(code, lineIndex, line.indexOf(targetName));
    if (isOffsetInPythonStringOrLineComment(code, targetOffset)) continue;
    const assignmentIndex = findTopLevelKeywordAssignmentIndex(line);
    if (assignmentIndex <= 0) continue;
    const rawValue = stripTrailingPythonLineComment(line.slice(assignmentIndex + 1));
    if (rawValue) value = rawValue;
  }

  return value;
}

/** Replaces one constructor keyword argument on the given declaration line. */
export function replaceConstructorKwargInCode(code: string, declarationLineIndex: number, kwargName: string, newValue: string): string {
  const span = findConstructorKwargSpan(code, declarationLineIndex, kwargName);
  if (!span) return code;
  return code.slice(0, span.startOffset) + `${kwargName}=${newValue}` + code.slice(span.endOffset);
}

/**
 * Column of the call's opening parenthesis on `lineIndex`, skipping parens inside strings and comments.
 * Returns `-1` when the line opens no call, so a non-constructor line cannot borrow a later statement's call.
 */
function findCallOpenParenColumn(code: string, lines: string[], lineIndex: number): number {
  const line = lines[lineIndex] ?? '';
  for (let column = line.indexOf('('); column >= 0; column = line.indexOf('(', column + 1)) {
    if (!isOffsetInPythonStringOrLineComment(code, offsetAtLineColumn(lines, lineIndex, column))) return column;
  }
  return -1;
}

/**
 * True when every top-level argument is a plain `name=value` keyword and the list carries no comments.
 *
 * Inserting is only safe for this shape. A positional argument may already bind the parameter being written
 * (`HighSpeedMotor(1)` binds `port`), so appending `port=5` would produce a duplicate binding that raises
 * `TypeError` at runtime, and comments make the insertion point ambiguous. Anything else fails closed.
 */
function constructorArgsAreSimpleKwargs(argsText: string): boolean {
  if (containsPythonLineCommentOutsideStrings(argsText)) return false;

  return splitTopLevelByComma(argsText).every((token) => {
    const trimmed = token.trim();
    if (!trimmed) return true;
    return findTopLevelKeywordAssignmentIndex(token) > 0;
  });
}

/**
 * Ensures the last argument line in `beforeClose` ends with a comma. Only a trailing whitespace-only line is
 * dropped, so blank lines the user wrote between the last argument and the closing parenthesis survive.
 * Callers guarantee the argument list holds no comments, so there is no comment to step over here.
 */
function appendPythonArgumentComma(beforeClose: string, hasExistingArgs: boolean): string {
  const lines = beforeClose.split('\n');
  // The final segment is the closing paren's own indent; the caller re-emits it.
  if (lines.length > 1 && !(lines[lines.length - 1] ?? '').trim()) lines.pop();
  if (!hasExistingArgs) return lines.join('\n');

  for (let lineIndex = lines.length - 1; lineIndex >= 0; lineIndex--) {
    const line = lines[lineIndex] ?? '';
    if (!line.trim()) continue;

    const trimmedCode = line.trimEnd();
    // Never append to the declaration line itself; a comma directly after `(` is a syntax error.
    if (!trimmedCode.endsWith(',') && !trimmedCode.endsWith('(')) {
      lines[lineIndex] = `${trimmedCode},${line.slice(trimmedCode.length)}`;
    }
    return lines.join('\n');
  }

  return lines.join('\n');
}

/**
 * Inserts `kwargName=newValue` into a constructor call span that does not already declare it.
 * Returns `null` when the call parentheses cannot be located or the argument list is not a plain keyword
 * list, so callers can tell an insert apart from a no-op.
 */
function insertKwargInConstructorText(
  text: string,
  openIndex: number,
  kwargName: string,
  newValue: string,
  formatCall?: (callText: string) => string
): string | null {
  const closeIndex = findMatchingDelimiterIndex(text, openIndex, '(', ')', { ignoreLineComments: true });
  if (closeIndex < 0) return null;

  const kwarg = `${kwargName}=${newValue}`;
  const argsText = text.slice(openIndex + 1, closeIndex);
  if (!constructorArgsAreSimpleKwargs(argsText)) return null;

  if (!argsText.includes('\n')) {
    const trimmedArgs = argsText.trimEnd();
    const separator = trimmedArgs.trim().length === 0 ? '' : trimmedArgs.endsWith(',') ? ' ' : ', ';
    const inserted = text.slice(0, closeIndex) + separator + kwarg + text.slice(closeIndex);
    return formatCall ? formatCall(inserted) : inserted;
  }

  const closeLineStart = text.lastIndexOf('\n', closeIndex) + 1;
  const closeIndent = text.slice(closeLineStart, closeIndex).match(/^\s*/)?.[0] ?? '';
  const firstArgLine = argsText.split('\n').find((line) => line.trim().length > 0);
  const argIndent = firstArgLine?.match(/^\s*/)?.[0] ?? `${closeIndent}    `;
  const beforeClose = appendPythonArgumentComma(text.slice(0, closeIndex), argsText.trim().length > 0);
  return `${beforeClose}\n${argIndent}${kwarg},\n${closeIndent}${text.slice(closeIndex)}`;
}

/**
 * Inserts a constructor keyword argument on the given declaration line, for constructors that omit it
 * (e.g. `intake = HighSpeedMotor()` relying on a framework default). Returns `null` when nothing was inserted.
 *
 * `formatCall` is injected rather than imported so this module stays free of the Ruff/WASM formatting layer,
 * which already depends on it. Pass `formatGeneratedPythonCall` to match the Monaco `addConstructorArg` output.
 */
export function insertConstructorKwargInCode(
  code: string,
  declarationLineIndex: number,
  kwargName: string,
  newValue: string,
  formatCall?: (callText: string) => string
): string | null {
  const lines = splitCodeLines(code);
  if (declarationLineIndex < 0 || declarationLineIndex >= lines.length) return null;

  const startOffset = offsetAtLineColumn(lines, declarationLineIndex, 0);
  const openColumn = findCallOpenParenColumn(code, lines, declarationLineIndex);
  if (openColumn < 0) return null;

  const endLineIndex = findConstructorCallEndLineIndex(lines, declarationLineIndex);
  const endOffset = offsetAtLineColumn(lines, endLineIndex, (lines[endLineIndex] ?? '').length);
  const constructorText = code.slice(startOffset, endOffset);

  const nextConstructorText = insertKwargInConstructorText(constructorText, openColumn, kwargName, newValue, formatCall);
  if (nextConstructorText === null || nextConstructorText === constructorText) return null;

  return code.slice(0, startOffset) + nextConstructorText + code.slice(endOffset);
}

export interface ConstructorKwargWriteResult {
  code: string;
  /** True when the constructor declares `kwargName=newValue` after the write, including when it already did. */
  applied: boolean;
}

/**
 * Writes a constructor kwarg on `parentName = ClassName(...)`, resolving the declaration by variable name
 * (stored card line numbers can be stale when code is inserted above the device). Replaces the kwarg when the
 * constructor already declares it, otherwise inserts it.
 *
 * Callers must branch on `applied` rather than comparing the returned code: an unresolvable declaration and a
 * kwarg that already holds the requested value both leave the source byte-identical, but only one of them means
 * the value is now declared.
 */
export function writeConstructorKwargInProgramCode(
  code: string,
  parentName: string,
  kwargName: string,
  newValue: string,
  fallbackLineIndex0Based?: number,
  constructorClassName?: string,
  formatCall?: (callText: string) => string
): ConstructorKwargWriteResult {
  const lines = splitCodeLines(code);
  const declLine = resolveConstructorDeclarationLineIndex(lines, parentName, {
    constructorClassName,
    fallbackLineIndex0Based,
  });
  if (declLine === null) return { code, applied: false };

  if (findConstructorKwargSpan(code, declLine, kwargName)) {
    return { code: replaceConstructorKwargInCode(code, declLine, kwargName, newValue), applied: true };
  }

  const inserted = insertConstructorKwargInCode(code, declLine, kwargName, newValue, formatCall);
  return inserted === null ? { code, applied: false } : { code: inserted, applied: true };
}

const REGISTER_CALLABLE_PARAMETER_KWARG = 'parameter';
const REGISTER_CALLABLE_DATATYPE_KWARG = 'datatype';
const REGISTER_CALLABLE_GETTER_KWARG = 'getter';

export interface RegisterCallableRegistration {
  /** Stream parameter name passed to `register_callable` (used for `DataStreamStore` requests and widget labels). */
  parameter: string;
}

function parseStringLiteralCallArgValue(rawArg: string): string | null {
  const trimmed = rawArg.trim();
  if (!isPlainPythonQuotedStringLiteral(trimmed) || trimmed.length < 2) return null;
  return parsePythonPlainStringLiteralValue(trimmed);
}

function hasNonEmptyRegisterCallableGetterArg(rawArg: string | undefined): boolean {
  return Boolean(rawArg?.trim());
}

/** Positional getter arg for `register_callable(parameter, datatype, getter)` only. */
function getRegisterCallablePositionalGetterArg(args: string[]): string | undefined {
  const firstArg = args[0]?.trim() ?? '';
  if (!isPlainPythonQuotedStringLiteral(firstArg)) return undefined;

  const secondArg = args[1]?.trim() ?? '';
  if (!isPlainPythonQuotedStringLiteral(secondArg)) return undefined;

  return args[2];
}

/** True when a `datatype=` kwarg with a string literal value is present. */
function extractRegisterCallableHasDatatypeFromKeywordArgs(args: string[]): boolean {
  for (const entry of args) {
    const assignmentIndex = findTopLevelKeywordAssignmentIndex(entry);
    if (assignmentIndex < 0) continue;
    const kwargName = entry.slice(0, assignmentIndex).trim();
    if (kwargName !== REGISTER_CALLABLE_DATATYPE_KWARG) continue;
    return parseStringLiteralCallArgValue(entry.slice(assignmentIndex + 1)) !== null;
  }

  return false;
}

/** True when a datatype argument is present (positional second arg or `datatype=` kwarg). */
function extractRegisterCallableHasDatatypeArg(argsText: string): boolean {
  const args = splitTopLevelByComma(argsText);
  const firstArg = args[0]?.trim() ?? '';
  if (isPlainPythonQuotedStringLiteral(firstArg) && isPlainPythonQuotedStringLiteral(args[1]?.trim() ?? '')) {
    return true;
  }

  return extractRegisterCallableHasDatatypeFromKeywordArgs(args);
}

/** Stream key from positional first arg or `parameter="..."` kwargs on `register_callable`. */
function extractRegisterCallableParameterFromArgs(argsText: string): string | null {
  const args = splitTopLevelByComma(argsText);
  const fromPositional = parseStringLiteralCallArgValue(args[0] ?? '');
  if (fromPositional !== null) return fromPositional;

  for (const entry of args) {
    const assignmentIndex = findTopLevelKeywordAssignmentIndex(entry);
    if (assignmentIndex < 0) continue;
    const kwargName = entry.slice(0, assignmentIndex).trim();
    if (kwargName !== REGISTER_CALLABLE_PARAMETER_KWARG) continue;
    return parseStringLiteralCallArgValue(entry.slice(assignmentIndex + 1));
  }

  return null;
}

/** True when a getter argument is present (positional or `getter=`), regardless of expression shape. */
function extractRegisterCallableHasGetterArg(argsText: string): boolean {
  const args = splitTopLevelByComma(argsText);
  const positionalGetter = getRegisterCallablePositionalGetterArg(args);
  if (positionalGetter !== undefined) {
    return hasNonEmptyRegisterCallableGetterArg(positionalGetter);
  }

  for (const entry of args) {
    const assignmentIndex = findTopLevelKeywordAssignmentIndex(entry);
    if (assignmentIndex < 0) continue;
    const kwargName = entry.slice(0, assignmentIndex).trim();
    if (kwargName !== REGISTER_CALLABLE_GETTER_KWARG) continue;
    return hasNonEmptyRegisterCallableGetterArg(entry.slice(assignmentIndex + 1));
  }

  return false;
}

function extractRegisterCallableRegistrationFromArgs(argsText: string): RegisterCallableRegistration | null {
  const parameter = extractRegisterCallableParameterFromArgs(argsText);
  if (parameter === null || !extractRegisterCallableHasDatatypeArg(argsText) || !extractRegisterCallableHasGetterArg(argsText)) {
    return null;
  }
  return { parameter };
}

/**
 * Scans Python code for `DataStreamStore.register_callable(...)` calls, including the legacy
 * `DataStreamStore.get_instance().register_callable(...)` form, and returns stream parameters
 * for each valid registration.
 */
export function parseRegisterCallableRegistrations(code: string): RegisterCallableRegistration[] {
  const seen = new Set<string>();
  const registrations: RegisterCallableRegistration[] = [];
  // `[ \t]*` around the dots, not `\s*`: Python allows spaces there, and the sibling DataStreamStore
  // scanners accept them. They match per line, so their `\s*` cannot span lines; this scan runs over
  // the whole source, where `\s*` would match a receiver and method on separate lines.
  const re = /DataStreamStore[ \t]*\.[ \t]*(?:get_instance[ \t]*\([ \t]*\)[ \t]*\.[ \t]*)?register_callable[ \t]*\(/g;
  let match: RegExpExecArray | null;

  while ((match = re.exec(code)) !== null) {
    // Left boundary, so a user's own `MyDataStreamStore.register_callable(...)` is not read as a
    // framework call, while a module qualifier (`sphero.DataStreamStore.`) still matches. Checked
    // manually rather than with `(?<!...)`: lookbehind is unsupported before Safari 16.4, esbuild
    // cannot transpile it, and Vite's default `modules` target includes Safari 14 — an unsupported
    // regex literal would fail to parse and take the Dashboard route down on that target.
    if (isPythonIdentifierChar(match.index > 0 ? code[match.index - 1] ?? '' : '')) continue;

    // Commented-out and docstring registrations must not become phantom streams in the drawer.
    // Classifying the offset in the original source keeps every index 1:1 with `code`; stripping
    // comments first would drop the comment-terminating newline and silently join two lines.
    if (isOffsetInPythonStringOrLineComment(code, match.index)) continue;

    const openParenIndex = match.index + match[0].length - 1;
    const span = readBalancedDelimiterSpan(code, openParenIndex, '(', ')');
    if (!span) continue;

    const registration = extractRegisterCallableRegistrationFromArgs(span.inner);
    if (registration !== null && !seen.has(registration.parameter)) {
      seen.add(registration.parameter);
      registrations.push(registration);
    }
  }

  return registrations;
}

/** Stream parameters registered via `register_callable` in project code. */
export function buildRegisteredCustomCallableParametersFromCode(code: string): ReadonlySet<string> {
  return new Set(parseRegisterCallableRegistrations(code).map((registration) => registration.parameter));
}

/**
 * Scans Python code for `DataStreamStore.register_callable(...)` calls, including the legacy
 * `get_instance()` form, and returns stream parameter names (for `DataStreamStore` requests
 * and widget `dataKey`).
 */
export function parseRegisterCallableNames(code: string): string[] {
  return parseRegisterCallableRegistrations(code).map((registration) => registration.parameter);
}

// ---------------------------------------------------------------------------
// Module-execution flow walker
// ---------------------------------------------------------------------------

export type PythonModuleFlowStatementKind =
  | 'if'
  | 'elif'
  | 'else'
  | 'for'
  | 'while'
  | 'with'
  | 'try'
  | 'except'
  | 'finally'
  | 'def'
  | 'class'
  | 'other';

/** Exit statements found in a `while` loop's suite (nested `def`/`class` suites excluded). */
export interface PythonLoopSuiteExits {
  /** A `break` whose nearest enclosing loop is this loop (a `break` in a nested loop does not count). */
  hasBreak: boolean;
  hasReturn: boolean;
  hasRaise: boolean;
}

export interface PythonModuleFlowStatement {
  kind: PythonModuleFlowStatementKind;
  /** 0-based first physical line of the logical statement. */
  startLineIndex: number;
  /** 0-based last physical line (multiline calls, backslash/bracket continuations). */
  endLineIndex: number;
  /** Indent of the first physical line, tabs normalized to 4 columns. */
  indentColumn: number;
  /** Trimmed, comment-stripped text of the first physical line. */
  headerText: string;
  /**
   * True when the statement is inside a `def` or `class` suite. Class-suite statements
   * execute at module time and are always emitted; def-suite statements are deferred
   * and emitted only with `includeDefSuites`.
   */
  insideDefOrClass: boolean;
  /** Present only for module-flow `while` statements. */
  loopExits?: PythonLoopSuiteExits;
}

interface PythonLogicalLineSpan {
  startLineIndex: number;
  endLineIndex: number;
}

/**
 * Groups physical lines into logical statements: bracket continuations, backslash
 * continuations, and triple-quoted strings extend a statement across lines.
 * Blank and comment-only lines between statements are skipped.
 */
function groupPythonLogicalLines(lines: string[]): PythonLogicalLineSpan[] {
  const spans: PythonLogicalLineSpan[] = [];
  const stringState = createPythonStringScanState();
  let bracketDepth = 0;
  let backslashContinuation = false;
  let current: PythonLogicalLineSpan | null = null;

  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    const line = lines[lineIndex] ?? '';
    const startsNewStatement = !stringState.quote && bracketDepth === 0 && !backslashContinuation;

    if (startsNewStatement) {
      const trimmed = line.trim();
      if (!trimmed || trimmed.startsWith('#')) continue;
      current = { startLineIndex: lineIndex, endLineIndex: lineIndex };
      spans.push(current);
    } else if (current) {
      current.endLineIndex = lineIndex;
    }

    backslashContinuation = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i] ?? '';
      const stringChars = scanPythonStringLiteralAt(stringState, line, i);
      if (stringChars > 0) {
        i += stringChars - 1;
        continue;
      }
      if (ch === '#') break;
      if (ch === '(' || ch === '[' || ch === '{') bracketDepth++;
      else if (ch === ')' || ch === ']' || ch === '}') {
        if (bracketDepth > 0) bracketDepth--;
      } else if (ch === '\\' && i === line.length - 1) {
        backslashContinuation = true;
      }
    }

    // Non-triple strings cannot span a raw newline: a trailing backslash escapes the
    // newline (string continues), anything else is malformed source we tolerate.
    if (stringState.quote && !stringState.triple) {
      if (stringState.escaped) {
        stringState.escaped = false;
      } else {
        resetPythonStringScanState(stringState);
      }
    }
  }

  return spans;
}

const PYTHON_COMPOUND_KEYWORD_RE = /^(?:async\s+)?(if|elif|else|for|while|with|try|except|finally|def|class)\b/;

/** Replaces string-literal contents with spaces so keyword scans cannot match inside strings. */
export function replacePythonStringContentsWithSpaces(text: string): string {
  const stringState = createPythonStringScanState();
  let result = '';
  for (let i = 0; i < text.length; i++) {
    const stringChars = scanPythonStringLiteralAt(stringState, text, i);
    if (stringChars > 0) {
      result += ' '.repeat(stringChars);
      i += stringChars - 1;
      continue;
    }
    result += text[i] ?? '';
  }
  return result;
}

/**
 * Returns the inline suite text after a compound statement's block colon
 * (e.g. `pass` for `while True: pass`), or null when the suite is an indented block.
 */
export function extractInlineSuiteText(headerText: string): string | null {
  let parenDepth = 0;
  let bracketDepth = 0;
  let braceDepth = 0;
  const stringState = createPythonStringScanState();

  for (let i = 0; i < headerText.length; i++) {
    const ch = headerText[i] ?? '';
    const stringChars = scanPythonStringLiteralAt(stringState, headerText, i);
    if (stringChars > 0) {
      i += stringChars - 1;
      continue;
    }
    if (ch === '(') parenDepth++;
    else if (ch === ')' && parenDepth > 0) parenDepth--;
    else if (ch === '[') bracketDepth++;
    else if (ch === ']' && bracketDepth > 0) bracketDepth--;
    else if (ch === '{') braceDepth++;
    else if (ch === '}' && braceDepth > 0) braceDepth--;
    else if (ch === ':' && parenDepth === 0 && bracketDepth === 0 && braceDepth === 0) {
      if (headerText[i + 1] === '=') {
        i++;
        continue;
      }
      const inline = headerText.slice(i + 1).trim();
      return inline.length > 0 ? inline : null;
    }
  }

  return null;
}

interface OpenPythonBlock {
  kind: PythonModuleFlowStatementKind;
  indentColumn: number;
  /** Set only for `while` blocks so exit statements in the suite can be attributed. */
  whileStatement: PythonModuleFlowStatement | null;
  /**
   * Raised exception names (null = unknown) pending on `try` blocks, resolved when
   * the block is popped: a same-indent `except` whose handler plausibly matches
   * catches them, anything else (finally-only tries, dedents, end of file)
   * propagates them outward as real loop exits. Survivors of a mismatched handler
   * carry onto the `except` block itself so chained clauses can still catch them.
   */
  pendingRaises: (string | null)[];
}

/** Raised exception name from a raise statement/segment, or null when unknowable. */
const RAISE_NAME_RE = /^raise\b(?:\s+([A-Za-z_][\w.]*))?/;

/**
 * Simple-name handler matching for `except` clauses. Bare `except`, `Exception`,
 * and `BaseException` catch everything; otherwise a known raised name must appear
 * verbatim in the handler list. Unknown raised names are presumed caught (the
 * pre-existing approximation); subclass relationships are not modeled, so a
 * subclass catch under a different name degrades the loop to a warning.
 */
function parseExceptHandler(headerText: string): { catchesAll: boolean; names: Set<string> } {
  const withoutKeyword = headerText.replace(/^except\b\*?/, '');
  const colonIndex = withoutKeyword.indexOf(':');
  const clause = (colonIndex >= 0 ? withoutKeyword.slice(0, colonIndex) : withoutKeyword)
    .replace(/\bas\s+[A-Za-z_]\w*\s*$/, '')
    .trim()
    .replace(/^\(/, '')
    .replace(/\)$/, '')
    .trim();
  if (!clause) return { catchesAll: true, names: new Set() };
  const names = new Set(
    clause
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean)
  );
  return { catchesAll: names.has('Exception') || names.has('BaseException'), names };
}

function exceptHandlerCatches(headerText: string, raisedName: string | null): boolean {
  const handler = parseExceptHandler(headerText);
  return handler.catchesAll || raisedName === null || handler.names.has(raisedName);
}

/**
 * Walks outward from a raise, marking each while it escapes. Reaching a `try`
 * suite parks the raise (with its exception name) as pending on that try; whether
 * it escapes further is decided when the try is popped. Raises inside `except`/
 * `finally` suites pass through those blocks and keep propagating outward.
 */
function propagateRaise(stack: OpenPythonBlock[], raisedName: string | null) {
  for (let i = stack.length - 1; i >= 0; i--) {
    const block = stack[i];
    if (!block) continue;
    if (block.kind === 'try') {
      block.pendingRaises.push(raisedName);
      return;
    }
    if (block.kind === 'while' && block.whileStatement?.loopExits) block.whileStatement.loopExits.hasRaise = true;
  }
}

function markLoopExit(stack: OpenPythonBlock[], exit: 'break' | 'return') {
  if (exit === 'break') {
    for (let i = stack.length - 1; i >= 0; i--) {
      const block = stack[i];
      if (!block) continue;
      if (block.kind === 'for') return;
      if (block.kind === 'while') {
        if (block.whileStatement?.loopExits) block.whileStatement.loopExits.hasBreak = true;
        return;
      }
    }
    return;
  }

  // `return` propagates out of every enclosing loop.
  for (const block of stack) {
    if (block.kind === 'while' && block.whileStatement?.loopExits) {
      block.whileStatement.loopExits.hasReturn = true;
    }
  }
}

function applyInlineSuiteLoopExits(statement: PythonModuleFlowStatement, inlineSuiteText: string) {
  if (!statement.loopExits) return;
  const scannable = replacePythonStringContentsWithSpaces(inlineSuiteText);
  if (/\bbreak\b/.test(scannable)) statement.loopExits.hasBreak = true;
  if (/\breturn\b/.test(scannable)) statement.loopExits.hasReturn = true;
  if (/\braise\b/.test(scannable)) statement.loopExits.hasRaise = true;
}

export interface CollectModuleFlowStatementsOptions {
  /**
   * Also emit statements inside `def` suites (flagged `insideDefOrClass: true`).
   * Loop-exit analysis still covers only module-flow `while` loops. Used by consumers
   * that need string-safe knowledge of deferred statements (e.g. anchor suppression).
   */
  includeDefSuites?: boolean;
}

/**
 * Walks logical statements in module-execution order: statements inside inline compound
 * suites (`if`/`elif`/`else`/`for`/`while`/`with`/`try`/`except`/`finally`) are included
 * at any indent because they run at module execution time. `class` suites are also
 * walked — Python executes class bodies at definition time. Only `def` suites are
 * skipped, because function bodies run when called (unless `includeDefSuites` is set).
 * The `def` statements themselves are included.
 *
 * `while` statements carry {@link PythonLoopSuiteExits} describing `break`/`return`/`raise`
 * usage in their suite. One-line suites are covered wherever they appear: `while x: break`
 * marks the loop itself, and a nested `if done: break` marks the nearest enclosing loop.
 * A `break` in a nested `for` (block or inline) is consumed by that `for`.
 */
export function collectModuleFlowStatements(code: string, options: CollectModuleFlowStatementsOptions = {}): PythonModuleFlowStatement[] {
  const lines = splitCodeLines(code);
  const statements: PythonModuleFlowStatement[] = [];
  const stack: OpenPythonBlock[] = [];
  // A raise in an inline `try: ...` suite resolves against the next statement:
  // a matching same-indent except catches it, anything else lets it escape.
  let pendingInlineTryRaise: { indentColumn: number; raisedName: string | null } | null = null;

  for (const span of groupPythonLogicalLines(lines)) {
    const headerLine = lines[span.startLineIndex] ?? '';
    const indentColumn = pythonLineIndentColumn(headerLine);
    const headerText = stripTrailingPythonLineComment(headerLine.trim());

    const keywordMatch = headerText.match(PYTHON_COMPOUND_KEYWORD_RE);
    const kind = (keywordMatch?.[1] as PythonModuleFlowStatementKind | undefined) ?? 'other';

    // Multiline compound headers need their full logical text for clause parsing:
    // inline suites and except handler lists can span continuation lines.
    const logicalHeaderText =
      span.endLineIndex === span.startLineIndex
        ? headerText
        : lines
            .slice(span.startLineIndex, span.endLineIndex + 1)
            .map((line) => stripTrailingPythonLineComment(line.trim()))
            .join(' ');

    if (pendingInlineTryRaise !== null) {
      const caughtByExcept =
        kind === 'except' &&
        indentColumn === pendingInlineTryRaise.indentColumn &&
        exceptHandlerCatches(logicalHeaderText, pendingInlineTryRaise.raisedName);
      if (!caughtByExcept) propagateRaise(stack, pendingInlineTryRaise.raisedName);
      pendingInlineTryRaise = null;
    }

    // Pending raises surviving a mismatched except handler carry onto the except
    // block itself, so chained clauses can still catch them.
    const carriedPendingRaises: (string | null)[] = [];
    while (stack.length > 0 && (stack[stack.length - 1]?.indentColumn ?? 0) >= indentColumn) {
      const popped = stack.pop();
      if (!popped || popped.pendingRaises.length === 0) continue;
      if (popped.kind !== 'try' && popped.kind !== 'except') continue;
      // The block's clauses are complete only when something at its own indent
      // follows; a matching except clause there catches pending raises, anything
      // else lets them escape into the enclosing blocks.
      if (kind === 'except' && indentColumn === popped.indentColumn) {
        const handler = parseExceptHandler(logicalHeaderText);
        for (const raisedName of popped.pendingRaises) {
          if (handler.catchesAll || raisedName === null || handler.names.has(raisedName)) continue;
          carriedPendingRaises.push(raisedName);
        }
      } else {
        for (const raisedName of popped.pendingRaises) propagateRaise(stack, raisedName);
      }
    }
    // Only def suites defer execution; class bodies execute at definition time.
    const insideDef = stack.some((block) => block.kind === 'def');
    const insideDefOrClass = insideDef || stack.some((block) => block.kind === 'class');

    const statement: PythonModuleFlowStatement = {
      kind,
      startLineIndex: span.startLineIndex,
      endLineIndex: span.endLineIndex,
      indentColumn,
      headerText,
      insideDefOrClass,
    };

    if (!insideDef) {
      if (kind === 'while') {
        statement.loopExits = { hasBreak: false, hasReturn: false, hasRaise: false };
      }
      statements.push(statement);

      if (kind === 'other') {
        // Exit statements can be semicolon siblings (`poll(); break`), including on
        // continuation lines of multiline statements, so the full logical statement
        // is scanned (strings blanked, comments stripped), not just its first line.
        const fullText = span.endLineIndex === span.startLineIndex ? headerText : lines.slice(span.startLineIndex, span.endLineIndex + 1).join('\n');
        const scannableStatement = replacePythonStringContentsWithSpaces(fullText).replace(/#[^\n]*/g, '');
        for (const segment of scannableStatement.split(';')) {
          const trimmed = segment.trim();
          if (/^break\b/.test(trimmed)) markLoopExit(stack, 'break');
          else if (/^return\b/.test(trimmed)) markLoopExit(stack, 'return');
          else {
            const raiseMatch = trimmed.match(RAISE_NAME_RE);
            if (raiseMatch) propagateRaise(stack, raiseMatch[1] ?? null);
          }
        }
      }
    } else if (options.includeDefSuites) {
      statements.push(statement);
    }

    if (keywordMatch) {
      // Inline suites can sit on the final continuation line of a multiline header
      // (`if (\n    done\n): break`), so extraction uses the full logical text.
      const inlineSuiteText = extractInlineSuiteText(logicalHeaderText);
      if (inlineSuiteText !== null) {
        if (!insideDef && kind !== 'def' && kind !== 'class') {
          const scannable = replacePythonStringContentsWithSpaces(inlineSuiteText);
          if (kind === 'while') {
            applyInlineSuiteLoopExits(statement, inlineSuiteText);
          } else if (kind !== 'for' && /\bbreak\b/.test(scannable)) {
            // A break in the inline suite of if/else/with/try/... exits the nearest
            // enclosing loop; a break in a `for` inline suite is consumed by that for.
            markLoopExit(stack, 'break');
          }
          if (/\breturn\b/.test(scannable)) markLoopExit(stack, 'return');
          const inlineRaiseMatch = scannable.match(/\braise\b(?:\s+([A-Za-z_][\w.]*))?/);
          if (inlineRaiseMatch) {
            const raisedName = inlineRaiseMatch[1] ?? null;
            // An inline `try: raise ...` resolves against the following clause: a
            // matching except catches it, a finally-only try lets it escape.
            if (kind === 'try') pendingInlineTryRaise = { indentColumn, raisedName };
            else propagateRaise(stack, raisedName);
          }
          if (kind === 'except') {
            for (const raisedName of carriedPendingRaises) propagateRaise(stack, raisedName);
            carriedPendingRaises.length = 0;
          }
        }
      } else {
        stack.push({
          kind,
          indentColumn,
          whileStatement: !insideDef && kind === 'while' ? statement : null,
          pendingRaises: kind === 'except' ? carriedPendingRaises : [],
        });
      }
    }
  }

  // End of file closes every open block; pending raises with no except escaped.
  if (pendingInlineTryRaise !== null) propagateRaise(stack, pendingInlineTryRaise.raisedName);
  while (stack.length > 0) {
    const popped = stack.pop();
    if (!popped || popped.pendingRaises.length === 0) continue;
    if (popped.kind !== 'try' && popped.kind !== 'except') continue;
    for (const raisedName of popped.pendingRaises) propagateRaise(stack, raisedName);
  }

  return statements;
}
